import { useEffect, useState, useCallback, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { useQueryClient } from 'react-query';
import {
  OutgoingEventProductDto,
  OutgoingSectionDto,
  IncomingCreateEventStandardProductDto,
  IncomingUpdateEventStandardProductDto,
  IncomingReorderProductsDto,
  IncomingCreateEventBundleProductDto,
  IncomingUpdateEventBundleProductDto
} from 'api/dtos/events/products';
import {
  useGetEventProducts,
  useCreateEventStandardProduct,
  useUpdateEventStandardProduct,
  useDeleteEventProduct,
  useReorderEventProducts,
  useCreateEventBundleProduct,
  useUpdateEventBundleProduct
} from 'api/queries/events/products';
import { ErrorResponse } from 'legacy/common/utils/error';

/**
 * Custom hook for managing event products with API integration.
 * Handles state management and API calls for all product-related operations.
 */
const useManageProducts = (eventId: string) => {
  const queryClient = useQueryClient();

  // API query for fetching products
  const { data: productsData, isLoading: isLoadingProducts } =
    useGetEventProducts({
      eventId
    });

  // Local state
  const [products, setProducts] = useState<Array<OutgoingEventProductDto>>([]);
  const [sections, setSections] = useState<Array<OutgoingSectionDto>>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] =
    useState<OutgoingEventProductDto | null>(null);

  // Add bypass ref to prevent useEffect from overriding state updates from addProductToSection
  const bypassProductsSync = useRef(false);

  // API mutations
  const createStandardProductMutation = useCreateEventStandardProduct();
  const createBundleProductMutation = useCreateEventBundleProduct();
  const updateStandardProductMutation = useUpdateEventStandardProduct();
  const updateBundleProductMutation = useUpdateEventBundleProduct();
  const deleteProductMutation = useDeleteEventProduct();
  const reorderProductsMutation = useReorderEventProducts();

  // Sync state with API data
  useEffect(() => {
    if (
      productsData?.items &&
      !isEqual(products, productsData.items) &&
      !bypassProductsSync.current
    ) {
      setProducts(productsData.items);
    }

    if (productsData?.sections && !isEqual(sections, productsData.sections)) {
      setSections(productsData.sections);
    }
  }, [productsData]);

  /**
   * Updates the query cache with current state
   */
  const updateQueryCache = useCallback(() => {
    queryClient.setQueryData(['eventProducts', { eventId }], {
      items: products,
      sections
    });
  }, [eventId, queryClient, products, sections]);

  /**
   * Invalidates relevant queries to refresh data
   */
  const invalidateProductQueries = useCallback(() => {
    updateQueryCache();
    queryClient.invalidateQueries(['eventProducts', { eventId }]);
  }, [eventId, queryClient, updateQueryCache]);

  /**
   * Adds a new standard product
   */
  const addStandardProduct = useCallback(
    (
      productData: IncomingCreateEventStandardProductDto,
      options?: {
        onSuccess?: (data: OutgoingEventProductDto) => void;
        onError?: (error: ErrorResponse) => void;
      }
    ) => {
      createStandardProductMutation.mutate(
        {
          eventId,
          product: productData
        },
        {
          onSuccess: (newProduct) => {
            // Update state with the confirmed data from the server
            setProducts((prevProducts) => [...prevProducts, newProduct]);
            invalidateProductQueries();
            options?.onSuccess?.(newProduct);
          },
          onError: (error) => {
            options?.onError?.(error);
          }
        }
      );

      return null;
    },
    [createStandardProductMutation, eventId, invalidateProductQueries]
  );

  /**
   * Adds a new bundle product
   */
  const addBundleProduct = useCallback(
    (
      productData: IncomingCreateEventBundleProductDto,
      options?: {
        onSuccess?: (data: OutgoingEventProductDto) => void;
        onError?: (error: ErrorResponse) => void;
      }
    ) => {
      createBundleProductMutation.mutate(
        {
          eventId,
          product: productData
        },
        {
          onSuccess: (newProduct) => {
            setProducts((prevProducts) => [...prevProducts, newProduct]);
            invalidateProductQueries();
            options?.onSuccess?.(newProduct);
          },
          onError: (error) => {
            options?.onError?.(error);
          }
        }
      );

      return null;
    },
    [createBundleProductMutation, eventId, invalidateProductQueries]
  );

  /**
   * Updates an existing standard product
   */
  const updateStandardProduct = useCallback(
    (
      productId: string,
      productData: IncomingUpdateEventStandardProductDto,
      options?: {
        onSuccess?: (data: OutgoingEventProductDto) => void;
        onError?: (error: ErrorResponse) => void;
      }
    ) => {
      // Find current product
      const currentProduct = products.find((p) => p.id === productId);
      if (!currentProduct) {
        return null;
      }

      // Make API call
      updateStandardProductMutation.mutate(
        {
          eventId,
          productId,
          product: productData
        },
        {
          onSuccess: (updatedProduct) => {
            // Update state only after successful API response
            setProducts((prevProducts) =>
              prevProducts.map((product) =>
                product.id === productId ? updatedProduct : product
              )
            );
            // Set last created product ID to enable scrolling to the updated product
            invalidateProductQueries();
            options?.onSuccess?.(updatedProduct);
          },
          onError: (error) => {
            options?.onError?.(error);
          }
        }
      );

      return currentProduct;
    },
    [updateStandardProductMutation, eventId, invalidateProductQueries, products]
  );

  /**
   * Updates an existing bundle product
   */
  const updateBundleProduct = useCallback(
    (
      productId: string,
      productData: IncomingUpdateEventBundleProductDto,
      options?: {
        onSuccess?: (data: OutgoingEventProductDto) => void;
        onError?: (error: ErrorResponse) => void;
      }
    ) => {
      // Find current product
      const currentProduct = products.find((p) => p.id === productId);
      if (!currentProduct) {
        return null;
      }

      // Make API call
      updateBundleProductMutation.mutate(
        {
          eventId,
          productId,
          product: productData
        },
        {
          onSuccess: (updatedProduct) => {
            // Update state only after successful API response
            setProducts((prevProducts) =>
              prevProducts.map((product) =>
                product.id === productId ? updatedProduct : product
              )
            );
            // Set last created product ID to enable scrolling to the updated product
            invalidateProductQueries();
            options?.onSuccess?.(updatedProduct);
          },
          onError: (error) => {
            options?.onError?.(error);
          }
        }
      );

      return currentProduct;
    },
    [updateBundleProductMutation, eventId, invalidateProductQueries, products]
  );

  /**
   * Deletes a standard product
   */
  const deleteStandardProduct = useCallback(
    (
      productId: string,
      options?: {
        onSuccess?: () => void;
        onError?: (error: ErrorResponse) => void;
      }
    ) => {
      const product = products.find((p) => p.id === productId);
      if (!product || product.type === 'bundle') {
        return false;
      }

      deleteProductMutation.mutate(
        {
          eventId,
          productId
        },
        {
          onSuccess: () => {
            setProducts((prevProducts) =>
              prevProducts.filter((product) => product.id !== productId)
            );

            invalidateProductQueries();
            options?.onSuccess?.();
          },
          onError: (error) => {
            options?.onError?.(error);
          }
        }
      );

      return true;
    },
    [deleteProductMutation, eventId, invalidateProductQueries, products]
  );

  /**
   * Deletes a bundle product
   */
  const deleteBundleProduct = useCallback(
    (
      productId: string,
      options?: {
        onSuccess?: () => void;
        onError?: (error: ErrorResponse) => void;
      }
    ) => {
      const product = products.find((p) => p.id === productId);
      if (!product || product.type !== 'bundle') {
        return false;
      }

      // Make API call
      deleteProductMutation.mutate(
        {
          eventId,
          productId
        },
        {
          onSuccess: () => {
            // Update state only after successful API response
            setProducts((prevProducts) =>
              prevProducts.filter((product) => product.id !== productId)
            );
            invalidateProductQueries();
            options?.onSuccess?.();
          },
          onError: (error) => {
            options?.onError?.(error);
          }
        }
      );

      return true;
    },
    [deleteProductMutation, eventId, invalidateProductQueries, products]
  );

  /**
   * Toggles a standard product's active state
   */
  const toggleStandardProductActive = useCallback(
    (
      productId: string,
      isActive: boolean,
      options?: {
        onSuccess?: () => void;
        onError?: (error: ErrorResponse) => void;
      }
    ) => {
      const product = products.find((p) => p.id === productId);
      if (!product || product.type === 'bundle') {
        return null;
      }

      const updateData: IncomingUpdateEventStandardProductDto = {
        name: product.name,
        description: product.description,
        price: product.price,
        isPrivate: product.isPrivate,
        isActive,
        isVisible: product.isVisible,
        imageUrl: product.imageUrl,
        startDate: product.startDate,
        endDate: product.endDate,
        maxQuantity: product.maxQuantity,
        purchaseRequirements: {
          requiresApproval:
            product.purchaseRequirements?.requiresApproval || false,
          questions:
            product.purchaseRequirements?.questions?.map((q) => ({
              id: q.id,
              question: q.question
            })) || [],
          requiredOrganizationIds:
            product.purchaseRequirements?.requiredOrganizationIds || [],
          requiredAssociationIds:
            product.purchaseRequirements?.requiredAssociationIds || []
        }
      };

      // Make API call
      updateStandardProductMutation.mutate(
        {
          eventId,
          productId,
          product: updateData
        },
        {
          onSuccess: (updatedProduct) => {
            setProducts((prevProducts) =>
              prevProducts.map((p) => (p.id === productId ? updatedProduct : p))
            );
            invalidateProductQueries();
            options?.onSuccess?.();
          },
          onError: (error) => {
            options?.onError?.(error);
          }
        }
      );

      return product;
    },
    [products, updateStandardProductMutation, eventId, invalidateProductQueries]
  );

  /**
   * Toggles a bundle product's active state
   */
  const toggleBundleProductActive = useCallback(
    (
      productId: string,
      isActive: boolean,
      options?: {
        onSuccess?: () => void;
        onError?: (error: ErrorResponse) => void;
      }
    ) => {
      const product = products.find((p) => p.id === productId);
      if (!product || product.type !== 'bundle') {
        return null;
      }

      const updateData: IncomingUpdateEventBundleProductDto = {
        name: product.name,
        description: product.description,
        price: product.price,
        isPrivate: product.isPrivate,
        isActive,
        imageUrl: product.imageUrl,
        startDate: product.startDate,
        endDate: product.endDate,
        components: product.components || [],
        isVisible: product.isVisible
      };

      // Make API call
      updateBundleProductMutation.mutate(
        {
          eventId,
          productId,
          product: updateData
        },
        {
          onSuccess: (updatedProduct) => {
            setProducts((prevProducts) =>
              prevProducts.map((p) => (p.id === productId ? updatedProduct : p))
            );
            invalidateProductQueries();
            options?.onSuccess?.();
          },
          onError: (error) => {
            options?.onError?.(error);
          }
        }
      );

      return product;
    },
    [products, updateBundleProductMutation, eventId, invalidateProductQueries]
  );

  /**
   * Toggles a standard product's visibility
   */
  const toggleStandardProductVisibility = useCallback(
    (
      productId: string,
      options?: {
        onSuccess?: () => void;
        onError?: (error: ErrorResponse) => void;
      }
    ) => {
      const product = products.find((p) => p.id === productId);
      if (!product || product.type === 'bundle') {
        return null;
      }

      const updateData: IncomingUpdateEventStandardProductDto = {
        name: product.name,
        description: product.description,
        price: product.price,
        isPrivate: product.isPrivate,
        isActive: product.isActive,
        isVisible: !product.isVisible,
        imageUrl: product.imageUrl,
        startDate: product.startDate,
        endDate: product.endDate,
        maxQuantity: product.maxQuantity,
        purchaseRequirements: {
          requiresApproval:
            product.purchaseRequirements?.requiresApproval || false,
          questions:
            product.purchaseRequirements?.questions?.map((q) => ({
              id: q.id,
              question: q.question
            })) || [],
          requiredOrganizationIds:
            product.purchaseRequirements?.requiredOrganizationIds || [],
          requiredAssociationIds:
            product.purchaseRequirements?.requiredAssociationIds || []
        }
      };

      // Make API call
      updateStandardProductMutation.mutate(
        {
          eventId,
          productId,
          product: updateData
        },
        {
          onSuccess: (updatedProduct) => {
            setProducts((prevProducts) =>
              prevProducts.map((p) => (p.id === productId ? updatedProduct : p))
            );
            invalidateProductQueries();
            options?.onSuccess?.();
          },
          onError: (error) => {
            options?.onError?.(error);
          }
        }
      );

      return product;
    },
    [products, updateStandardProductMutation, eventId, invalidateProductQueries]
  );

  /**
   * Toggles a bundle product's visibility
   */
  const toggleBundleProductVisibility = useCallback(
    (
      productId: string,
      options?: {
        onSuccess?: () => void;
        onError?: (error: ErrorResponse) => void;
      }
    ) => {
      const product = products.find((p) => p.id === productId);
      if (!product || product.type !== 'bundle') {
        return null;
      }

      // Create updated product data
      const updateData: IncomingUpdateEventBundleProductDto = {
        name: product.name,
        description: product.description,
        price: product.price,
        isPrivate: product.isPrivate,
        isActive: product.isActive,
        imageUrl: product.imageUrl,
        startDate: product.startDate,
        endDate: product.endDate,
        components: product.components || [],
        isVisible: !product.isVisible
      };

      // Make API call
      updateBundleProductMutation.mutate(
        {
          eventId,
          productId,
          product: updateData
        },
        {
          onSuccess: (updatedProduct) => {
            setProducts((prevProducts) =>
              prevProducts.map((p) => (p.id === productId ? updatedProduct : p))
            );
            invalidateProductQueries();
            options?.onSuccess?.();
          },
          onError: (error) => {
            options?.onError?.(error);
          }
        }
      );

      return product;
    },
    [products, updateBundleProductMutation, eventId, invalidateProductQueries]
  );

  /**
   * Adds a standard product to a specific section
   */
  const addStandardProductToSection = useCallback(
    (
      sectionId: string,
      productData: IncomingCreateEventStandardProductDto,
      options?: {
        onSuccess?: (data: OutgoingEventProductDto) => void;
        onError?: (error: ErrorResponse) => void;
      }
    ) => {
      // Set bypass flag to true
      bypassProductsSync.current = true;

      createStandardProductMutation.mutate(
        {
          eventId,
          product: productData
        },
        {
          onSuccess: (newProduct) => {
            const productsInTargetSection = products.filter(
              (p) => (p.sectionId || 'default') === (sectionId || 'default')
            );

            const newSortIndex =
              productsInTargetSection.length > 0
                ? Math.max(
                    ...productsInTargetSection.map((p) => p.sortIndex || 0)
                  ) + 1
                : 0;

            const productsToReorder = products.map((p) => ({
              productId: p.id,
              sortIndex: p.sortIndex || 0,
              sectionId: p.sectionId
            }));

            productsToReorder.push({
              productId: newProduct.id,
              sortIndex: newSortIndex,
              sectionId: sectionId === 'default' ? undefined : sectionId
            });

            const reorderData: IncomingReorderProductsDto = {
              products: productsToReorder,
              sections: sections.map((s) => ({
                id: s.id,
                sortIndex: s.sortIndex || 0
              }))
            };

            reorderProductsMutation.mutate(
              {
                eventId,
                data: reorderData
              },
              {
                onSuccess: () => {
                  // Reset bypass flag once operation is complete
                  bypassProductsSync.current = false;
                  invalidateProductQueries();
                  options?.onSuccess?.(newProduct);
                },
                onError: (error) => {
                  bypassProductsSync.current = false;
                  options?.onError?.(error);
                }
              }
            );
          },
          onError: (error) => {
            // Reset bypass flag on error
            bypassProductsSync.current = false;
            options?.onError?.(error);
          }
        }
      );

      return null;
    },
    [
      createStandardProductMutation,
      products,
      sections,
      reorderProductsMutation,
      eventId,
      invalidateProductQueries
    ]
  );

  /**
   * Adds a bundle product to a specific section
   */
  const addBundleProductToSection = useCallback(
    (
      sectionId: string,
      productData: IncomingCreateEventBundleProductDto,
      options?: {
        onSuccess?: (data: OutgoingEventProductDto) => void;
        onError?: (error: ErrorResponse) => void;
      }
    ) => {
      bypassProductsSync.current = true;

      createBundleProductMutation.mutate(
        {
          eventId,
          product: productData
        },
        {
          onSuccess: (newProduct) => {
            const productsInTargetSection = products.filter(
              (p) => (p.sectionId || 'default') === (sectionId || 'default')
            );
            const newSortIndex =
              productsInTargetSection.length > 0
                ? Math.max(
                    ...productsInTargetSection.map((p) => p.sortIndex || 0)
                  ) + 1
                : 0;

            const productsToReorder = products.map((p) => ({
              productId: p.id,
              sortIndex: p.sortIndex || 0,
              sectionId: p.sectionId
            }));

            productsToReorder.push({
              productId: newProduct.id,
              sortIndex: newSortIndex,
              sectionId: sectionId === 'default' ? undefined : sectionId
            });

            const reorderData: IncomingReorderProductsDto = {
              products: productsToReorder,
              sections: sections.map((s) => ({
                id: s.id,
                sortIndex: s.sortIndex || 0
              }))
            };

            // Make API call to reorder
            reorderProductsMutation.mutate(
              {
                eventId,
                data: reorderData
              },
              {
                onSuccess: () => {
                  // Reset bypass flag once operation is complete
                  bypassProductsSync.current = false;
                  invalidateProductQueries();
                  options?.onSuccess?.(newProduct);
                },
                onError: (error) => {
                  bypassProductsSync.current = false;
                  options?.onError?.(error);
                }
              }
            );
          },
          onError: (error) => {
            // Reset bypass flag on error
            bypassProductsSync.current = false;
            options?.onError?.(error);
          }
        }
      );

      return null;
    },
    [
      createBundleProductMutation,
      products,
      sections,
      reorderProductsMutation,
      eventId,
      invalidateProductQueries
    ]
  );

  /**
   * Reorders products and sections
   * Optimistic updates for reorder, introducing rollback if error
   */
  const reorderProducts = useCallback(
    (
      productsToReorder: {
        productId: string;
        sortIndex: number;
        sectionId?: string;
      }[],
      sectionsToReorder: { id: string; sortIndex: number }[],
      options?: {
        onSuccess?: () => void;
        onError?: (error: ErrorResponse) => void;
      }
    ) => {
      let previousProducts: OutgoingEventProductDto[] = [];
      let previousSections: OutgoingSectionDto[] = [];

      const reorderData: IncomingReorderProductsDto = {
        products: productsToReorder,
        sections: sectionsToReorder
      };

      // Optimistic update for products
      setProducts((prevProducts) => {
        previousProducts = [...prevProducts];
        return prevProducts.map((product) => {
          const reorderItem = productsToReorder.find(
            (p) => p.productId === product.id
          );
          if (!reorderItem) {
            return product;
          }

          return {
            ...product,
            sortIndex: reorderItem.sortIndex,
            sectionId: reorderItem.sectionId
          };
        });
      });

      // Optimistic update for sections
      setSections((prevSections) => {
        previousSections = [...prevSections];
        return prevSections.map((section) => {
          const reorderItem = sectionsToReorder.find(
            (s) => s.id === section.id
          );
          if (!reorderItem) {
            return section;
          }

          return {
            ...section,
            sortIndex: reorderItem.sortIndex
          };
        });
      });

      // Make API call
      reorderProductsMutation.mutate(
        {
          eventId,
          data: reorderData
        },
        {
          onSuccess: () => {
            options?.onSuccess?.();
          },
          onError: (error) => {
            // Rollback both products and sections to previous state
            setProducts(previousProducts);
            setSections(previousSections);
            options?.onError?.(error);
          }
        }
      );

      return true;
    },
    [reorderProductsMutation, eventId, invalidateProductQueries]
  );

  /**
   * Deletes a section and moves its products to the default section
   * Optimistic updates for delete section, introducing rollback if error
   */
  const deleteSection = useCallback(
    (
      sectionId: string,
      options?: {
        onSuccess?: (deletedProducts: OutgoingEventProductDto[]) => void;
        onError?: (error: ErrorResponse) => void;
      }
    ) => {
      let previousProducts: OutgoingEventProductDto[] = [];
      let previousSections: OutgoingSectionDto[] = [];

      const normalizedDefaultProducts = products
        .filter((p) => p.sectionId === undefined)
        .map((product, index) => ({
          ...product,
          sortIndex: index
        }));

      const normalizedDeletedProducts = products
        .filter((p) => p.sectionId === sectionId)
        .map((product, index) => ({
          ...product,
          sortIndex: normalizedDefaultProducts.length + index
        }));

      const productsToUpdate = [
        ...normalizedDefaultProducts,
        ...normalizedDeletedProducts
      ].map((product) => ({
        productId: product.id,
        sortIndex: product.sortIndex || 0,
        sectionId:
          product.sectionId === sectionId ? undefined : product.sectionId
      }));

      const sectionsToUpdate = sections
        .filter((s) => s.id !== sectionId)
        .map((s) => ({
          id: s.id,
          sortIndex: s.sortIndex || 0
        }));

      const reorderData: IncomingReorderProductsDto = {
        products: productsToUpdate,
        sections: sectionsToUpdate
      };

      // Optimistic update for products
      setProducts((prevProducts) => {
        previousProducts = [...prevProducts];
        return prevProducts.map((product) =>
          product.sectionId === sectionId
            ? { ...product, sectionId: undefined }
            : product
        );
      });

      // Optimistic update for sections
      setSections((prevSections) => {
        previousSections = [...prevSections];
        return prevSections.filter((section) => section.id !== sectionId);
      });

      // Make API call
      reorderProductsMutation.mutate(
        {
          eventId,
          data: reorderData
        },
        {
          onSuccess: () => {
            options?.onSuccess?.(normalizedDeletedProducts);
          },
          onError: (error) => {
            // Rollback both products and sections to previous state
            setProducts(previousProducts);
            setSections(previousSections);
            options?.onError?.(error);
          }
        }
      );

      return true;
    },
    [
      products,
      sections,
      reorderProductsMutation,
      eventId,
      invalidateProductQueries
    ]
  );

  // Computed/derived state
  const sortedProducts = products?.sort((a, b) => {
    if (a.isPrivate === b.isPrivate) {
      return 0;
    }
    return a.isPrivate ? 1 : -1;
  });

  const publicProducts = sortedProducts.filter((p) => !p.isPrivate);
  const privateProducts = sortedProducts.filter((p) => p.isPrivate);

  return {
    // Data
    products,
    sections,
    publicProducts,
    privateProducts,
    sortedProducts,
    selectedProduct,
    selectedIndex,
    isLoading: isLoadingProducts,

    // State setters
    setSelectedProduct,
    setSelectedIndex,

    // API operations
    addStandardProduct,
    addBundleProduct,
    updateStandardProduct,
    updateBundleProduct,
    deleteStandardProduct,
    deleteBundleProduct,
    toggleStandardProductActive,
    toggleBundleProductActive,
    toggleStandardProductVisibility,
    toggleBundleProductVisibility,
    addStandardProductToSection,
    addBundleProductToSection,
    reorderProducts,
    deleteSection,
    invalidateProductQueries
  };
};

export default useManageProducts;
